/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { When } from "react-if";
import { z } from "zod";
import { Button, Input, Label, Spacer, theme } from "@nordcloud/gnui";
import { Application, UpdateApplicationInput } from "~/generated/graphql";
import { Sidebar } from "~/components";
import { FormGroup, stringRequired } from "~/components/Forms";
import { useDisclosure } from "~/hooks";
import { useUpdateApplication } from "~/views/applications/hooks/useUpdateApplication/useUpdateApplication";
import { MAX_DISPLAY_NAME_LENGTH, MAX_DESCRIPTION_LENGTH } from "../constants";
import { SelectUsersSidebar } from "./SelectUsersSidebar";

export const schema = z.object({
  id: stringRequired("id"),
  displayName: stringRequired("displayName").max(MAX_DISPLAY_NAME_LENGTH, {
    message: `Application Name is too long (max: ${MAX_DISPLAY_NAME_LENGTH} characters)`,
  }),
  owner: stringRequired("owner"),
  description: z
    .string()
    .max(MAX_DESCRIPTION_LENGTH, {
      message: `Description is too long (max: ${MAX_DESCRIPTION_LENGTH} characters)`,
    })
    .optional()
    .nullable(),
});

type State = {
  owner: string;
  contactPerson: string;
};

type Props = {
  application: Application;
};

export function ApplicationEdit({ application }: Props) {
  const { isOpen, open, close } = useDisclosure();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    {
      owner: application.owner ?? "",
      contactPerson: application.contacts?.[0] ?? "",
    }
  );

  const { handleSubmit, register, formState, reset, clearErrors, setValue } =
    useForm({
      resolver: zodResolver(schema),
      defaultValues: {
        id: application.id,
        displayName: application.displayName ?? application.name,
        owner: application.owner ?? "",
        description: application.description,
      },
    });

  useEffect(() => {
    reset({
      id: application.id,
      displayName: application.displayName ?? application.name,
      description: application.description,
    });
    updateState({
      owner: application.owner ?? "",
      contactPerson: application.contacts?.[0] ?? "",
    });
  }, [application, reset]);

  useEffect(() => {
    if (application.owner) {
      setValue("owner", application.owner);
    }
  }, [isOpen, application, setValue]);

  const clean = () => {
    reset();
    clearErrors();
    close();
  };

  const [updateApplication, { loading }] = useUpdateApplication({
    onSuccess: () => {
      clean();
    },
  });

  const onSubmit = async (data: UpdateApplicationInput) => {
    const displayName = data?.displayName?.trim();
    const description = data?.description?.trim();

    const updateApplicationInput: UpdateApplicationInput = {
      id: data.id,
      displayName: displayName ? displayName : undefined,
      description: description ? description : undefined,
      owner: state.owner ? state.owner : undefined,
      contacts: state.contactPerson ? [state.contactPerson] : undefined,
    };

    await updateApplication(updateApplicationInput);
  };

  return (
    <>
      <Button
        aria-label={`Edit application ${
          application.displayName ?? application.name
        }`}
        icon="edit"
        size="md"
        severity="low"
        onClick={open}
      />
      <When condition={isOpen}>
        <Sidebar title="Edit Application" isOpen={isOpen} onClick={clean}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup error={formState.errors["displayName"]}>
              <Label name="Application Name" htmlFor="displayName" required />
              <input type="hidden" {...register("id")} value={application.id} />
              <Input
                id="displayName"
                placeholder="Application Name"
                disabled={loading}
                {...register("displayName")}
              />
            </FormGroup>
            <FormGroup error={formState.errors?.owner}>
              <Label name="Application Owner" htmlFor="owner" required />
              <SelectUsersSidebar
                id="owner"
                placeholder="Select Application Owner"
                sidebarTitle="Select Application Owner"
                inputValue={state.owner}
                isLoading={loading}
                onSelect={(subject) => {
                  updateState({ owner: subject });
                  setValue("owner", subject);
                }}
                isError={formState.errors?.owner && "danger"}
              />
            </FormGroup>
            <FormGroup>
              <Label name="Contact Person" htmlFor="contact" />
              <SelectUsersSidebar
                id="contact"
                placeholder="Select Contact Person"
                sidebarTitle="Select Contact Person"
                inputValue={state.contactPerson}
                isLoading={loading}
                onSelect={(subject) => {
                  updateState({ contactPerson: subject });
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label name="Description" htmlFor="description" />
              <Input
                id="description"
                placeholder="Provide Application Description"
                disabled={loading}
                {...register("description")}
              />
            </FormGroup>
            <Spacer height={theme.spacing.spacing02} />
            <Button
              type="submit"
              disabled={loading}
              initialState={loading ? "loading" : "success"}
            >
              Apply
            </Button>
          </form>
        </Sidebar>
      </When>
    </>
  );
}
